"use client";

import { useEffect, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  getSiteGlueOnboardingHrefWithWebsite,
  trackSiteGlueCta,
  trackSiteGlueEvent,
  type SiteGlueCtaLocation,
} from "~/lib/siteglue/cta-config";
import { normalizeMarketplaceWebsiteUrl } from "~/lib/marketplace/website-suggestions";
import { cn } from "~/lib/utils";

export const AI_PHONE_SYSTEM_WEBSITE_SESSION_KEY = "siteglue-ai-phone-system-website";

export function readAiPhoneSystemWebsiteFromSession(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(AI_PHONE_SYSTEM_WEBSITE_SESSION_KEY)?.trim();
    return stored ? normalizeMarketplaceWebsiteUrl(stored) : null;
  } catch {
    return null;
  }
}

export function persistAiPhoneSystemWebsite(websiteUrl: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(AI_PHONE_SYSTEM_WEBSITE_SESSION_KEY, websiteUrl);
  } catch {
    /* ignore */
  }
}

export function buildAiPhoneSystemOnboardingHref(websiteUrl: string): string {
  const url = new URL(getSiteGlueOnboardingHrefWithWebsite(websiteUrl));
  // Phone System landing → Associate (phone/email channels) by default.
  url.searchParams.set("plan", "associate");
  url.searchParams.set("campaign", "ai-phone-system");
  if (!url.searchParams.has("utm_campaign")) {
    url.searchParams.set("utm_campaign", "ai_phone_system");
  }
  url.searchParams.set("referrer", "ai-phone-system");
  return url.href;
}

export type AiPhoneSystemBuildLocation = Extract<
  SiteGlueCtaLocation,
  "hero" | "feature_section" | "final_cta" | "demo_card" | "industry_page"
>;

export function startAiPhoneSystemOnboarding(
  websiteUrl: string,
  location: AiPhoneSystemBuildLocation,
): void {
  const normalized = normalizeMarketplaceWebsiteUrl(websiteUrl);
  if (!normalized) return;
  persistAiPhoneSystemWebsite(normalized);
  const href = buildAiPhoneSystemOnboardingHref(normalized);
  trackSiteGlueCta({
    action: "trish",
    label: "Build My AI Phone System",
    location,
    destination: href,
    extra: {
      website: normalized,
      event: "ai_phone_system_build_click",
      campaign: "ai-phone-system",
    },
  });
  trackSiteGlueEvent("ai_phone_system_build_click", {
    page: "ai-phone-system",
    cta_location: location,
    website: normalized,
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
  });
  trackSiteGlueEvent("ai_phone_system_onboarding_start", {
    page: "ai-phone-system",
    cta_location: location,
    website: normalized,
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
  });
  window.location.assign(href);
}

export function scrollToAiPhoneSystemBuildForm(): void {
  document.getElementById("build-ai-phone-system")?.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });
  window.setTimeout(() => {
    document.getElementById("ai-phone-system-website-hero")?.focus();
  }, 350);
}

type BuildAiPhoneSystemFormProps = {
  location: Extract<SiteGlueCtaLocation, "hero" | "feature_section" | "final_cta">;
  tone?: "dark" | "light";
  className?: string;
  /** Wrapper id for scroll targets (hero form uses `build-ai-phone-system`). */
  id?: string;
  inputId?: string;
};

export function BuildAiPhoneSystemForm({
  location,
  tone = "light",
  className,
  id,
  inputId = "ai-phone-system-website",
}: BuildAiPhoneSystemFormProps) {
  const [website, setWebsite] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const stored = readAiPhoneSystemWebsiteFromSession();
    if (stored) setWebsite(stored.replace(/^https?:\/\//i, ""));
  }, []);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const normalized = normalizeMarketplaceWebsiteUrl(website);
    if (!normalized) {
      setError("Enter a valid organization website (e.g. yourorganization.com).");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      startAiPhoneSystemOnboarding(normalized, location);
    } catch {
      setSubmitting(false);
      setError("Something went wrong. Please try again.");
    }
  };

  const isDark = tone === "dark";

  return (
    <div id={id} className={cn("w-full max-w-xl", className)}>
      <form
        onSubmit={onSubmit}
        data-testid={`form-ai-phone-system-website-${location}`}
      >
        <label htmlFor={inputId} className="sr-only">
          Organization website
        </label>
        <div
          className={cn(
            "flex flex-col overflow-hidden rounded-xl border bg-white shadow-lg focus-within:ring-2 focus-within:ring-[#4f81bd]/80 sm:flex-row",
            isDark
              ? "border-white/15 shadow-black/20"
              : "border-slate-200 shadow-slate-200/60",
          )}
        >
          <Input
            id={inputId}
            type="text"
            inputMode="url"
            autoComplete="url"
            placeholder="https://www.yourorganization.com"
            value={website}
            onChange={(e) => {
              setWebsite(e.target.value);
              if (error) setError(null);
            }}
            disabled={submitting}
            className="h-12 flex-1 rounded-none border-0 border-b border-slate-100 bg-transparent text-base text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-0 sm:h-14 sm:border-b-0"
          />
          <Button
            type="submit"
            size="lg"
            disabled={submitting}
            className="h-12 shrink-0 rounded-none bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-800 sm:h-14 sm:px-5 sm:text-base"
            data-testid={`button-ai-phone-system-submit-${location}`}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Building…
              </>
            ) : (
              "Build My AI Phone System"
            )}
          </Button>
        </div>
        <p
          className={cn(
            "mt-2 text-sm",
            isDark ? "text-slate-400" : "text-slate-500",
          )}
        >
          15-day trial. A card is required up front and the plan price starts on day 15.
        </p>
        {error ? (
          <p className={cn("mt-1 text-sm", isDark ? "text-red-300" : "text-red-600")}>{error}</p>
        ) : null}
      </form>
    </div>
  );
}
